import { Button } from '@heroui/react';
import { ArrowUpRight, LogOut, UserRound } from 'lucide-react';
import './NoAccessView.css';

/** Un umbral arquitectónico: acompaña el mensaje sin convertir los permisos en una alarma. */
function WorkspaceEntrance() {
  return (
    <svg className="no-access-entrance" viewBox="0 0 420 300" fill="none" aria-hidden="true">
      <ellipse cx="210" cy="260" rx="167" ry="29" fill="#ffffff06" />
      <path d="m52 243 158-64 158 64-158 64Z" fill="#ffffff05" />
      <path d="m91 243 119-48 119 48-119 48Z" stroke="#ffffff12" />
      <path d="M210 195v96m-119-48h238m-178-24 119 48m-119 0 119-48" stroke="#ffffff12" />
      <path d="M130 66h100a52 52 0 0 1 52 52v138H130Z" fill="#D8D2E9" />
      <path d="m282 118 26-12v138l-26 12Z" fill="#8C849F" />
      <path d="m130 66 26-12h100a52 52 0 0 1 52 52l-26 12a52 52 0 0 0-52-52Z" fill="#F6F5F4" />
      <path d="M150 91h72a39 39 0 0 1 39 39v126H150Z" fill="#30283E" />
      <path d="M164 107h54a28 28 0 0 1 28 28v121h-82Z" fill="#211A2D" />
      <path d="m164 107 58 21v107l-58 21Z" fill="#A698C7" />
      <path d="m164 107 58 21v107l-58 21" stroke="#D8CEED" />
      <path d="M207 174v18" stroke="#F6F5F4" strokeWidth="3" strokeLinecap="round" />
      <path d="m150 256 111-1 26 13-112 1Z" fill="#EBE6F3" />
      <path d="m175 269 112-1v6l-112 1Z" fill="#AAA1BC" />
      <path d="M99 186v59m-9 0h18" stroke="#B5ADC5" strokeWidth="2" />
      <path d="M99 206c-28-13-26-48-26-48s30 7 26 48Z" fill="#9583B2" />
      <path d="M99 219c26-10 26-40 26-40s-28 3-26 40Z" fill="#C3B5DB" />
      <circle cx="322" cy="68" r="24" stroke="#C3B5DB" strokeOpacity=".25" />
      <path d="M315 68h14m-7-7v14" stroke="#C3B5DB" strokeWidth="1.5" />
    </svg>
  );
}

interface NoAccessViewProps {
  email: string;
  roles: string[];
  erpUrl?: string;
  onLogout?: () => void;
}

export function NoAccessView({ email, roles, erpUrl, onLogout }: NoAccessViewProps) {
  return (
    <div className="no-access-page">
      <header className="no-access-header">
        <div className="no-access-brand">
          <span>
            <img src="/brand/eleva-logo.svg" alt="Eleva" className="logo-light" />
            <img src="/brand/eleva-logo-white.svg" alt="Eleva" className="logo-dark" />
          </span>
          <span className="no-access-product">
            Operativa
            <br />
            semanal
          </span>
        </div>
        <span className="no-access-header-note">El espacio de tu oficina</span>
      </header>

      <main className="no-access-main">
        <aside className="no-access-story" aria-labelledby="no-access-story-title">
          <p className="no-access-eyebrow">
            <span /> Una oficina. Un equipo.
          </p>
          <h2 id="no-access-story-title">
            Cada equipo,
            <br />
            <span>su espacio.</span>
          </h2>
          <p className="no-access-story-description">
            Una visión compartida para decidir
            <br />y avanzar cada semana.
          </p>
          <WorkspaceEntrance />
          <div className="no-access-story-footer">
            <span>Prioridades</span>
            <i />
            <span>Acuerdos</span>
            <i />
            <span>Seguimiento</span>
          </div>
        </aside>

        <section className="no-access-content" aria-labelledby="no-access-title">
          <p className="no-access-kicker">Acceso al panel</p>
          <h1 id="no-access-title">Esta cuenta no tiene acceso al panel.</h1>
          <p className="no-access-description">
            Has iniciado sesión correctamente. Esta cuenta necesita un rol autorizado para abrir la reunión
            operativa.
          </p>

          <div className="no-access-account" aria-label="Tu cuenta en Eleva">
            <div className="no-access-account-heading">
              <span className="no-access-avatar">
                <UserRound size={19} strokeWidth={1.5} aria-hidden="true" />
              </span>
              <div>
                <span className="no-access-account-label">Has iniciado sesión como</span>
                <p className="no-access-email">{email}</p>
              </div>
            </div>
            <dl className="no-access-roles">
              <dt>Roles en Eleva</dt>
              <dd>
                {roles.length > 0 ? (
                  <ul aria-label="Roles de tu cuenta">
                    {roles.map((role, index) => (
                      <li key={`${role}-${index}`}>{role}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="no-access-no-roles">Sin roles asignados</span>
                )}
              </dd>
            </dl>
          </div>

          <div className="no-access-guidance">
            <span className="no-access-guidance-marker" aria-hidden="true" />
            <div>
              <h2>¿Deberías poder entrar?</h2>
              <p>Pide que revisen los roles de esta cuenta en Eleva para habilitar tu acceso al panel.</p>
            </div>
          </div>

          {(erpUrl || onLogout) && (
            <div className="no-access-actions">
              {erpUrl && (
                <a className="no-access-return" href={erpUrl}>
                  Volver a Eleva <ArrowUpRight size={17} aria-hidden="true" />
                </a>
              )}
              {onLogout && (
                <Button className="no-access-logout" variant="ghost" onPress={onLogout}>
                  <LogOut size={16} aria-hidden="true" /> Cerrar sesión
                </Button>
              )}
            </div>
          )}
        </section>
      </main>

      <footer className="no-access-footer">
        <span>Hecho por Eleva. Para trabajar mejor.</span>
        <span>Operativa semanal</span>
      </footer>
    </div>
  );
}
